import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import { PageSEO } from "@/App";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_consultores-landing-1/artifacts/zr6j82m0_ayces-01.png";

const AvisoDePrivacidad = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-white text-neutral-900 min-h-screen flex flex-col">
      <PageSEO
        title="Aviso de Privacidad | AYCES - Actuarios y Consultores Estratégicos SC"
        description="Aviso de Privacidad de Actuarios y Consultores Estratégicos SC conforme a la Ley Federal de Protección de Datos Personales en Posesión de Particulares."
        path="/aviso-de-privacidad"
      />
      {/* Top bar */}
      <header className="border-b border-brand-silver/40 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="aviso-back-logo">
            <img
              src={LOGO_URL}
              alt="AYCES"
              className="h-14 lg:h-16 w-auto object-contain"
            />
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-brand-navy hover:text-brand-blue transition-colors"
            data-testid="aviso-back-link"
          >
            <ArrowLeft className="h-4 w-4" />
            Regresar al inicio
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="relative bg-[#F8F9FA] border-b border-brand-silver/40">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 py-16 lg:py-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="icon-ring h-12 w-12 rounded-sm flex items-center justify-center">
                <ShieldCheck className="h-5 w-5 text-white" />
              </div>
              <span className="text-xs tracking-[0.28em] uppercase text-brand-blue font-medium">
                Documento legal
              </span>
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-brand-navy tracking-tight leading-[1.05]">
              Aviso de Privacidad
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <main className="flex-1">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 py-16 lg:py-20">
          <article
            className="prose-aviso text-neutral-700 text-base leading-relaxed space-y-6"
            data-testid="aviso-content"
          >
            <p>
              Con fundamento en los artículos 15 y 16 de la Ley Federal de
              Protección de Datos Personales en Posesión de Particulares hacemos
              de su conocimiento que Actuarios y Consultores Estratégicos, S.C.
              es responsable de recabar sus datos personales, del uso que se le
              dé a los mismos y de su protección.
            </p>

            <p>
              Su información personal será utilizada para las siguientes
              finalidades: proveer los servicios y productos que ha solicitado;
              notificarle sobre nuevos servicios o productos que tengan
              relación con los ya contratados o adquiridos; comunicarle sobre
              cambios en los mismos; elaborar estudios y programas que son
              necesarios para determinar hábitos de consumo; realizar
              evaluaciones periódicas de nuestros productos y servicios a
              efecto de mejorar la calidad de los mismos; evaluar la calidad
              del servicio que brindamos, y en general, para dar cumplimiento a
              las obligaciones que hemos contraído con usted.
            </p>

            <p>
              Para las finalidades antes mencionadas, requerimos obtener los
              siguientes datos personales:
            </p>

            <ul className="list-disc pl-6 space-y-1 marker:text-brand-blue">
              <li>Nombre completo</li>
              <li>Teléfono</li>
              <li>Correo electrónico</li>
            </ul>

            <p>
              Es importante informarle que usted tiene derecho al Acceso,
              Rectificación y Cancelación de sus datos personales, a Oponerse
              al tratamiento de los mismos o a revocar el consentimiento que
              para dicho fin nos haya otorgado.
            </p>

            <p>
              Para ello, es necesario que envíe la solicitud en los términos
              que marca la Ley en su Art. 29 a Departamento de Protección de
              Datos Personales, o bien, se comunique al teléfono (33) 15 4223
              42 o vía correo electrónico a{" "}
              <a
                href="mailto:contacto@ayces.com.mx"
                className="text-brand-navy underline underline-offset-4 hover:text-brand-blue"
              >
                contacto@ayces.com.mx
              </a>
              , el cual solicitamos confirme vía telefónica para garantizar su
              correcta recepción.
            </p>

            <p>
              En algunos casos, podemos usar Cookies para recopilar información
              que permite la identificación personal, o que podría pasar a
              permitir la identificación personal, si la combinamos con otra
              información.
            </p>

            <p>
              Mientras navegas en Actuarios y Consultores estratégicos las
              redes de publicidad en línea con las que trabajamos pueden
              colocar cookies anónimas en el equipo, y el uso de tecnologías
              similares, con el fin de entender sus intereses en función de sus
              (anónimos) las actividades en línea, y así adaptar anuncios más
              relevantes a usted.
            </p>

            <p>
              En caso de que no desee de recibir mensajes promocionales de
              nuestra parte, puede enviarnos su solicitud por medio de la
              dirección electrónica:{" "}
              <a
                href="mailto:contacto@ayces.com.mx"
                className="text-brand-navy underline underline-offset-4 hover:text-brand-blue"
              >
                contacto@ayces.com.mx
              </a>
              .
            </p>

            <p className="border-l-2 border-brand-navy pl-6 bg-brand-gray/40 py-4 pr-4 italic">
              <strong className="not-italic font-semibold text-brand-navy">
                Importante:
              </strong>{" "}
              Cualquier modificación a este Aviso de Privacidad podrá
              consultarlo en{" "}
              <a
                href="https://www.protecciondedatospersonales.org/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-brand-navy underline underline-offset-4 hover:text-brand-blue"
              >
                www.protecciondedatospersonales.org/
              </a>
            </p>
          </article>
        </div>
      </main>

      {/* Footer mini */}
      <footer className="bg-brand-navy text-white py-6">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 flex flex-col sm:flex-row justify-between items-center gap-3 text-[11px] text-white/70">
          <div>
            © {new Date().getFullYear()} Actuarios y Consultores Estratégicos
            SC. Todos los derechos reservados.
          </div>
          <Link
            to="/"
            className="text-white/80 hover:text-white"
            data-testid="aviso-footer-home"
          >
            Volver al sitio
          </Link>
        </div>
      </footer>
    </div>
  );
};

export default AvisoDePrivacidad;
